import * as pdfjs from 'pdfjs-dist';
import { TextItem, validateTextItems, ParserErrorCode, ParserError } from '../types/pdf';
import { logger, LogContext } from '../utils/logger';

// PDF.js types
interface PDFTextContent {
  items: (PDFTextItem | PDFTextMarkedContent)[];
  styles?: { [key: string]: any };
}

interface PDFTextItem {
  str: string;
  transform: number[];
  fontName?: string;
  fontSize?: number;
  width?: number;
  height?: number;
}

interface PDFTextMarkedContent {
  type: string;
}

// Configure PDF.js worker - use local file without module imports
if (typeof window !== 'undefined') {
  // Set to the local file in the public directory
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
  console.log('PDF.js worker configured to use local file');
}

// Custom error for PDF extraction failures
export class PDFExtractionError extends Error implements ParserError {
  code: ParserErrorCode;
  details?: LogContext;

  constructor(message: string, code: ParserErrorCode = ParserErrorCode.EXTRACTION_FAILED, details?: LogContext) {
    super(message);
    this.name = 'PDFExtractionError';
    this.code = code;
    this.details = details;
  }
}

// Clean up text - remove excessive whitespace, etc.
function cleanText(text: string): string {
  return text
    .replace(/\s+/g, ' ')  // Replace multiple whitespaces with a single space
    .trim();               // Remove leading/trailing whitespace
}

/**
 * Extract plain text from a PDF file
 * @param file PDF file to extract text from
 * @returns Plain text content from PDF
 */
export async function extractTextFromPDF(file: File): Promise<string> {
  try {
    logger.info('Starting plain text PDF extraction', { fileName: file.name, fileSize: file.size });
    
    // Convert File to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    logger.info('Successfully converted file to ArrayBuffer', { size: arrayBuffer.byteLength });
    
    // Load document
    logger.info('Creating PDF loading task');
    const loadingTask = pdfjs.getDocument({ 
      data: arrayBuffer,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/'
    });
    
    logger.info('Waiting for PDF loading promise to resolve');
    const pdf = await loadingTask.promise;
    
    // Get total page count
    const numPages = pdf.numPages;
    logger.info('PDF loaded successfully', { pages: numPages, fileName: file.name });
    
    // Extract text from each page
    let fullText = '';
    
    for (let i = 1; i <= numPages; i++) {
      logger.info('Processing page', { page: i, totalPages: numPages });
      
      try {
        // Get page
        const page = await pdf.getPage(i);
        
        // Extract text content
        const textContent = await page.getTextContent();
        
        // Combine text items
        const pageText = textContent.items
          .filter(item => 'str' in item) // Filter out marked content
          .map(item => 'str' in item ? item.str : '') // Extract text
          .join(' ');
        
        fullText += pageText + '\n\n';
        logger.info('Successfully extracted text from page', { page: i, textLength: pageText.length });
      } catch (pageError) {
        logger.warn(`Error extracting text from page ${i}, continuing with other pages`, {
          error: pageError instanceof Error ? pageError.message : String(pageError),
          page: i
        });
        // Continue with other pages instead of failing completely
        fullText += `[Error extracting page ${i}]\n\n`;
      }
    }
    
    // Clean up text
    const cleanedText = cleanText(fullText);
    
    // Check if we got any meaningful text
    if (cleanedText.length < 10) {
      logger.warn('Extracted text is suspiciously short', { textLength: cleanedText.length, fileName: file.name });
      // Still return what we have, letting the caller decide if it's sufficient
    }
    
    logger.info('Successfully extracted plain text', { 
      fileName: file.name,
      charCount: cleanedText.length
    });
    
    return cleanedText;
  } catch (error) {
    // Log the error and throw a standardized error
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('Failed to extract text from PDF', { 
      fileName: file.name,
      fileSize: file.size,
      error: errorMessage,
      stack: error instanceof Error ? error.stack : undefined
    });
    
    // Try a fallback with a simpler approach if primary extraction fails
    try {
      logger.info('Attempting fallback text extraction', { fileName: file.name });
      
      // Create a simpler document loading configuration
      const loadingTask = pdfjs.getDocument({
        data: await file.arrayBuffer(),
        disableFontFace: true, // Disable custom fonts which can cause issues
        useSystemFonts: true // Use system fonts as fallback
      });
      
      const pdf = await loadingTask.promise;
      let fallbackText = '';
      
      // Get text with minimal processing
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items
          .filter(item => 'str' in item && (item as any).str.trim().length > 0)
          .map(item => 'str' in item ? (item as any).str : '')
          .join(' ');
        
        fallbackText += pageText + '\n\n';
      }
      
      if (fallbackText.length > 0) {
        logger.info('Fallback extraction succeeded', { textLength: fallbackText.length });
        return cleanText(fallbackText);
      }
    } catch (fallbackError) {
      logger.error('Fallback extraction also failed', {
        error: fallbackError instanceof Error ? fallbackError.message : String(fallbackError)
      });
    }
    
    // If we get here, both extraction methods failed
    throw new PDFExtractionError(`Failed to extract text from PDF: ${errorMessage}`);
  }
}

/**
 * Extract structured text items from a PDF file
 * @param file PDF file to extract text from
 * @returns Array of TextItem objects with position information
 */
export async function extractStructuredTextFromPDF(file: File): Promise<TextItem[]> {
  try {
    logger.info(`Starting structured PDF extraction`, { fileName: file.name });
    
    // Convert File to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load the PDF document
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    logger.info(`PDF loaded successfully`, { pages: pdf.numPages });
    
    // Extract text from all pages
    const textItems: TextItem[] = [];
    
    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        logger.info(`Processing page`, { page: i, totalPages: pdf.numPages });
        const page = await pdf.getPage(i);
        const content = await page.getTextContent() as PDFTextContent;
        
        // Extract text items with position info
        const pageItems = content.items
          .filter((item): item is PDFTextItem => 'str' in item && Boolean(item.str.trim()))
          .map((item) => {
            // Transform array contains: horizontal scaling, vertical skewing, horizontal skewing, 
            // vertical scaling, horizontal translation, vertical translation
            const [scaleX, , , scaleY, translateX, translateY] = item.transform;
            
            const textItem: TextItem = {
              text: item.str.trim(),
              x: parseFloat(translateX.toFixed(2)),
              y: parseFloat(translateY.toFixed(2)),
              font: item.fontName || 'unknown',
              bold: item.fontName?.toLowerCase().includes('bold') || false,
              pageNumber: i,
              width: item.width,
              height: item.height,
              fontSize: item.fontSize
            };
            return textItem;
          });
        
        if (pageItems.length > 0) {
          textItems.push(...pageItems);
          logger.info(`Extracted text items from page`, { page: i, count: pageItems.length });
        } else {
          logger.warn(`No text content found on page`, { page: i });
        }
      } catch (pageError) {
        const context: LogContext = { page: i, error: pageError instanceof Error ? pageError.message : String(pageError) };
        logger.error(`Error processing page`, context);
        throw new PDFExtractionError(
          `Failed to process page ${i}`,
          ParserErrorCode.EXTRACTION_FAILED,
          context
        );
      }
    }
    
    if (textItems.length === 0) {
      throw new PDFExtractionError(
        'No text content found in PDF',
        ParserErrorCode.EXTRACTION_FAILED
      );
    }
    
    // Validate all text items before returning
    const validatedItems = validateTextItems(textItems);
    
    logger.info(`Successfully extracted structured text items`, { count: validatedItems.length });
    if (validatedItems[0]) {
      logger.debug(`First text item preview`, { item: JSON.stringify(validatedItems[0]) });
    }
    
    return validatedItems;
  } catch (error) {
    const context: LogContext = {
      error: error instanceof Error ? error.message : String(error)
    };
    logger.error(`Structured PDF extraction failed`, context);
    
    if (error instanceof PDFExtractionError) {
      throw error;
    }
    
    throw new PDFExtractionError(
      error instanceof Error ? error.message : 'Failed to extract text from PDF',
      ParserErrorCode.UNKNOWN_ERROR,
      context
    );
  }
}
